import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Radio, Zap, Eye, Clock, CheckCircle, Building2, Cable, Wifi, Server, Cpu, Network, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import QuoteForm from '../components/QuoteForm';

const SuperiorAlarmG2Page = () => {
  // Products Grade 2 with Ajax official BLACK images
  const g2Products = [
    {
      name: "Hub Hybrid (4G)",
      fullName: "Superior Hub Hybrid (4G)",
      description: "Centrale hybride Fibra + Jeweller",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fhub_hybrid_black_eb78efaa15%402.png",
      specs: "200 appareils • Grade 2 • 4G/Ethernet"
    },
    {
      name: "Hub Hybrid 2",
      fullName: "Superior Hub Hybrid 2 (4G)",
      description: "Centrale hybride évoluée",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_hub_hybrid_2_black_52f36d51a5%402.png",
      specs: "250 appareils • 64 scénarios"
    },
    {
      name: "Hub 2 Plus",
      fullName: "Hub 2 Plus Jeweller",
      description: "Centrale sans fil avancée",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fhub_256999a1dd%402.png",
      specs: "200 appareils • Wi-Fi/4G/Ethernet"
    },
    {
      name: "DoorProtect",
      fullName: "Superior DoorProtect Jeweller",
      description: "Détecteur d'ouverture",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fdpsj_s_553d1e2fdd%402.png",
      specs: "Double reed • Anti-sabotage"
    },
    {
      name: "DoorProtect Plus",
      fullName: "Superior DoorProtect Plus Jeweller",
      description: "Détecteur ouverture + chocs",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fdpspj_s_7f05c12224%402.png",
      specs: "Chocs • Inclinaison • Reed"
    },
    {
      name: "MotionProtect",
      fullName: "Superior MotionProtect Jeweller",
      description: "Détecteur PIR",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmpsj_s_34e8d588e9%402.png",
      specs: "Portée 12m • Pet immunity"
    },
    {
      name: "MotionProtect Plus",
      fullName: "Superior MotionProtect Plus Jeweller",
      description: "Détecteur PIR + Micro-ondes",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmpspj_s_68b8826cc2%402.png",
      specs: "Double techno • Zéro fausse alarme"
    },
    {
      name: "MotionCam (PhOD)",
      fullName: "Superior MotionCam (PhOD) Jeweller",
      description: "Détecteur avec photo",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmcsj_s_a359914540%402.png",
      specs: "Photos HD • Vérification visuelle"
    },
    {
      name: "MotionCam HD",
      fullName: "Superior MotionCam HD (PhOD)",
      description: "Détecteur photo HD",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_motioncam_hd_phod_black_275b14e213%402.png",
      specs: "Photos HD 1280×720"
    },
    {
      name: "GlassProtect",
      fullName: "Superior GlassProtect Jeweller",
      description: "Détecteur bris de glace",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fgpsj_s_b2db014217%402.png",
      specs: "Microphone • 9m de portée"
    },
    {
      name: "CombiProtect",
      fullName: "Superior CombiProtect Jeweller",
      description: "Détecteur PIR + bris de glace",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fcpsj_s_17e605965d%402.png",
      specs: "2-en-1 • Micro + PIR"
    },
    {
      name: "KeyPad Plus",
      fullName: "KeyPad Plus Jeweller",
      description: "Clavier tactile sans fil",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fkeypad_plus_xl_be5d454dde%402.png",
      specs: "DESFire® • Pass/Tag"
    },
  ];

  const fibraAdvantages = [
    {
      title: "Bus 2 fils unique",
      description: "Alimentation et données sur un seul câble. Plus besoin de tirer des fils séparés.",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Ftwo_wires_6c40b61d59%402.jpg"
    },
    {
      title: "Jusqu'à 2000m de câblage",
      description: "Topologie en bus, étoile ou mixte selon vos besoins d'installation.",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2F2000m_3b89cf2ae8%402.jpg"
    },
    {
      title: "Vitesse 10x supérieure",
      description: "Transmission instantanée des événements et photos MotionCam.",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fspeed_10x_1e89b67cc2%402.jpg"
    },
    {
      title: "Immunité aux interférences",
      description: "Aucun risque de brouillage radio, communication ultra-fiable.",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fno_jamming_4d78e2f1a3%402.jpg"
    },
  ];

  const keyFeatures = [
    { icon: Cable, title: "Fibra + Jeweller", desc: "Hybride filaire et sans fil" },
    { icon: Clock, title: "Installation rapide", desc: "Configuration par app" },
    { icon: Zap, title: "Alarme instantanée", desc: "Transmission < 0.15s" },
    { icon: Eye, title: "Vérification photo", desc: "MotionCam HD" },
    { icon: Lock, title: "200+ dispositifs", desc: "Grande capacité" },
    { icon: Shield, title: "Certifié EN 50131", desc: "Grade 2" },
  ];

  const useCases = [
    { name: "Commerce & Retail", desc: "Boutiques, centres commerciaux", icon: "🛍️" },
    { name: "Bureaux & Tertiaire", desc: "Sièges sociaux, coworking", icon: "🏢" },
    { name: "Hôtellerie", desc: "Hôtels, restaurants", icon: "🏨" },
    { name: "Industrie", desc: "Usines, entrepôts", icon: "🏭" },
    { name: "Santé", desc: "Cliniques, cabinets", icon: "🏥" },
    { name: "Éducation", desc: "Écoles, universités", icon: "🎓" },
  ];

  return (
    <div className="min-h-screen py-12 bg-black" data-testid="superior-alarm-g2-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Alarme Filaire Professionnelle Grade 2 | Ajax Fibra - FGS Alarme Montpellier</title>
        <meta name="description" content="Alarme filaire Ajax Fibra Grade 2 pour entreprises. Système hybride filaire + sans fil, jusqu'à 2000m de câblage. Installation professionnelle Montpellier. Devis gratuit." />
        <meta name="keywords" content="alarme filaire, ajax fibra, alarme professionnelle, alarme entreprise, système filaire, alarme grade 2, installation alarme pro, hub hybrid, câblage alarme, sécurité entreprise montpellier" />
        <link rel="canonical" href="https://maprotection-ia.fr/superior-alarm-g2" />
      </Helmet>

      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden">
          <img 
            src="https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_xxl_d165f6741f%402.jpg" 
            alt="Solution Superior Grade 2"
            className="w-full h-[350px] md:h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent flex items-center">
            <div className="px-6 md:px-12 max-w-2xl">
              <p className="text-purple-400 text-sm font-medium tracking-wide uppercase mb-3">Technologie Fibra + Jeweller</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
                Superior Alarme <span className="text-purple-400">Grade 2</span>
              </h1>
              <p className="text-base md:text-lg text-white/50 mb-6 font-light">
                La technologie hybride Ajax pour les entreprises exigeantes. Combinez le filaire Fibra et le sans fil Jeweller.
              </p>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-fgs-primary/10 border border-fgs-primary/20 rounded-full">
                  <Shield className="w-4 h-4 text-fgs-primary" />
                  <span className="text-white/70 text-sm">Garantie 4 ans</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
                  <Building2 className="w-4 h-4 text-white/50" />
                  <span className="text-white/70 text-sm">Sur devis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Carousel */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <FeaturesCarousel items={keyFeatures} color="purple" />
      </section>

      {/* Technologie Fibra */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="p-6 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Cable className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-white tracking-tight">
                  Technologie Fibra
                </h2>
              </div>
              <p className="text-white/60 mb-6 leading-relaxed font-light">
                Le protocole Fibra transforme l'installation filaire traditionnelle. Un simple bus 2 fils 
                transporte à la fois l'alimentation et les données chiffrées vers tous les appareils. 
                Plus besoin de tirer des câbles séparés pour chaque détecteur.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium text-sm">Jusqu'à 2000m de câblage</span>
                    <p className="text-xs text-white/50">Topologie en étoile, bus ou mixte</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium text-sm">Vitesse 10x supérieure</span>
                    <p className="text-xs text-white/50">Transmission instantanée des photos MotionCam</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium text-sm">Immunité aux interférences</span>
                    <p className="text-xs text-white/50">Aucun risque de brouillage radio</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-black/50 p-6 md:p-10 flex items-center justify-center">
              <div className="grid grid-cols-2 gap-3 w-full">
                {useCases.map((use, i) => (
                  <div key={i} className="bg-white/[0.02] rounded-xl p-4 border border-white/5 hover:border-white/10 transition-colors">
                    <span className="text-xl mb-2 block">{use.icon}</span>
                    <h4 className="text-white font-medium text-sm mb-1">{use.name}</h4>
                    <p className="text-[10px] text-white/50">{use.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Communication hybride */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
            Le meilleur des deux mondes
          </h2>
          <p className="text-white/60 max-w-3xl mx-auto font-light">
            Les centrales Hub Hybrid combinent les protocoles Fibra (filaire) et Jeweller (sans fil) 
            pour une flexibilité maximale.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white/[0.02] border border-white/5 hover:border-purple-500/20 rounded-2xl p-6 transition-colors">
            <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
              <Cable className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Fibra (Filaire)</h3>
            <p className="text-sm text-white/60 mb-3">Bus 2 fils pour alimentation et données. Idéal pour les nouvelles constructions.</p>
            <ul className="text-xs text-white/50 space-y-1">
              <li>• Jusqu'à 2000m de câblage</li>
              <li>• Immunité au brouillage</li>
              <li>• Pas de batteries</li>
            </ul>
          </div>
          <div className="bg-white/[0.02] border border-white/5 hover:border-blue-500/20 rounded-2xl p-6 transition-colors">
            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
              <Wifi className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Jeweller (Sans fil)</h3>
            <p className="text-sm text-white/60 mb-3">Communication radio chiffrée 868 MHz. Parfait pour les extensions.</p>
            <ul className="text-xs text-white/50 space-y-1">
              <li>• Portée jusqu'à 2000m</li>
              <li>• Installation en minutes</li>
              <li>• Batteries longue durée</li>
            </ul>
          </div>
          <div className="bg-white/[0.02] border border-white/5 hover:border-green-500/20 rounded-2xl p-6 transition-colors">
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <Network className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Redondance totale</h3>
            <p className="text-sm text-white/60 mb-3">Triple canal : Ethernet + Wi-Fi + 4G LTE. Aucune faille possible.</p>
            <ul className="text-xs text-white/50 space-y-1">
              <li>• Basculement automatique</li>
              <li>• Anti-sabotage réseau</li>
              <li>• Toujours connecté</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Gamme complète Grade 2 - Carousel */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
            Gamme Superior Grade 2
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto font-light">
            Tous les dispositifs certifiés EN 50131 Grade 2 pour une protection professionnelle
          </p>
        </div>

        <ProductsCarousel products={g2Products} color="purple" />
      </section>

      {/* Application PRO */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-white mb-4 tracking-tight">
                Application PRO Desktop
              </h2>
              <p className="text-white/60 mb-6 font-light">
                Configurez et gérez les systèmes Ajax depuis votre ordinateur. L'application PRO Desktop 
                offre une interface puissante pour les installateurs professionnels.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-white/70 text-sm">Configuration avancée de tous les paramètres</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-white/70 text-sm">Diagnostic système complet</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-white/70 text-sm">Gestion multi-sites</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-white/70 text-sm">Rapports d'installation</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-purple-500/5 border border-purple-500/10 rounded-2xl p-8">
                <Cpu className="w-20 h-20 text-purple-400 mx-auto" />
                <p className="text-center text-white font-medium mt-4">PRO Desktop</p>
                <p className="text-center text-white/50 text-sm">Windows & macOS</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
              Demandez votre étude Grade 2
            </h2>
            <p className="text-fgs-text-muted mb-6">
              Nos experts dimensionneront votre installation selon vos besoins spécifiques. 
              FGS est partenaire certifié Ajax pour les installations professionnelles.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Audit gratuit de votre site</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Choix Fibra, Jeweller ou hybride</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Installation par techniciens certifiés</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Garantie 4 ans FGS incluse</span>
              </div>
            </div>
          </div>
          <div>
            <QuoteForm category="pro" />
          </div>
        </div>
      </section>
    </div>
  );
};

// Features Carousel Component
const FeaturesCarousel = ({ items, color = 'purple' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi]);

  const colorClasses = {
    purple: { icon: 'text-purple-400', border: 'hover:border-purple-500/40' },
    amber: { icon: 'text-amber-400', border: 'hover:border-amber-500/40' },
    cyan: { icon: 'text-cyan-400', border: 'hover:border-cyan-500/40' },
  };

  return (
    <div className="relative">
      <div className="hidden md:flex absolute -top-2 right-0 gap-2 z-10">
        <button onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all">
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {items.map((feature, i) => (
            <div key={i} className="flex-shrink-0 w-[200px] md:w-[220px]">
              <div className={`h-full bg-fgs-surface border border-fgs-border rounded-xl p-5 text-center ${colorClasses[color].border} transition-colors`}>
                <feature.icon className={`w-10 h-10 ${colorClasses[color].icon} mx-auto mb-3`} />
                <h3 className="font-heading text-sm font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-xs text-fgs-text-muted">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

// Products Carousel Component
const ProductsCarousel = ({ products, color = 'purple' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi]);

  const colorClasses = {
    purple: { border: 'hover:border-purple-500/40', text: 'text-purple-400' },
    amber: { border: 'hover:border-amber-500/40', text: 'text-amber-400' },
    cyan: { border: 'hover:border-cyan-500/40', text: 'text-cyan-400' },
  };

  return (
    <div className="relative">
      <div className="hidden md:flex absolute -top-12 right-0 gap-2 z-10">
        <button onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all">
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {products.map((product, i) => (
            <div key={i} className="flex-shrink-0 w-[180px] md:w-[200px]">
              <div className={`h-full bg-fgs-surface border border-fgs-border rounded-xl p-4 ${colorClasses[color].border} transition-all duration-300 group`}>
                <div className="bg-white rounded-lg p-3 mb-3 aspect-square flex items-center justify-center">
                  <img src={product.image} alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                </div>
                <h3 className="font-heading text-sm font-bold text-white mb-1">{product.name}</h3>
                <p className="text-xs text-fgs-text-muted mb-2">{product.description}</p>
                <p className={`text-[11px] ${colorClasses[color].text}`}>{product.specs}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

export default SuperiorAlarmG2Page;
